import { getValue } from 'mobx-async'

import { formatDate } from '@avantstay/backoffice-core'

import {
  AmenitiesFilter,
  BathroomsRangeFilter,
  BedroomsRangeFilter,
  DepositsOnHoldFilter,
  GuestsRangeFilter,
} from '@new/__global/components/Filters'
import { FilterConfig } from '@new/__global/components/FiltersFramework'
import {
  arrayParamsFormatterAndParser,
  booleanParamParser,
  formatQuantityRangePreview,
  optionsFilterPreviewFormatter,
  rangeParamParser,
} from '@new/__global/components/FiltersFramework/_utils'
import { BookingsTapeChartStore } from '@new/domains/bookingsTapeChart/stores/BookingsTapeChartStore'

import { BookingsColorMode } from '../__types'
import { formatDateRange, parseDateRange } from '../__utils'
import { ArrivalsModeFilter } from '../components/TapeChartFilters/ArrivalsModeFilter'
import { BookingIssuesFilter } from '../components/TapeChartFilters/BookingIssuesFilter'
import { ColorByFilter } from '../components/TapeChartFilters/ColorByFilter'
import { PaymentStatusFilter } from '../components/TapeChartFilters/PaymentStatusFilter'
import { RatesFilter } from '../components/TapeChartFilters/RatesFilter'
import { ReservationStatusFilter } from '../components/TapeChartFilters/ReservationStatusFilter'
import { ViewFromFilter } from '../components/TapeChartFilters/ViewFromFilter'
import { DEFAULT_VIEW_FROM_DATE, ISO_DATE_FORMAT } from '../config/contants'
import { AvailableDatesFilter, storeHolder } from '../internal'
import { tapeChartGridSizingStore } from '../stores/TapeChartGridSizingStore'

export enum HomeFilterIds {
  Amenities = 'amenityIds',
  Highlights = 'highlights',
  MinBathrooms = 'minBathrooms',
  MinBedrooms = 'minBedrooms',
  MinPets = 'minPets',
  Regions = 'regionIds',
  RevenueModel = 'revenueModel',
  RiskLevel = 'riskLevel',
  SeasonGroups = 'seasonGroupIds',
  ShowOffBoarded = 'withOffboarded',
  PetPolicy = 'petPolicy',
  DateRange = 'dateRange',
  PriceRange = 'priceRange',
  MinOccupancy = 'minOccupancy',
  Guests = 'guests',
  BedroomsRange = 'bedroomsRange',
  BathroomsRange = 'bathrooms',
  AddressRadius = 'addressRadius',
  AccountStatus = 'accountStatus',
  GuestReadinessStatus = 'guestReadinessStatus',
  ListingStatus = 'listingStatus',
}

export enum BookingFilterIds {}

export enum ViewStateFilterIds {
  BookingIssues = 'bookingIssues',
  ColorBy = 'colorBy',
  DepositsOnHold = 'depositsOnHold',
  ViewFrom = 'viewFrom',
  NightlyRates = 'rates',
  PaymentStatus = 'paymentStatus',
  ReservationStatus = 'status',
  ArrivalsMode = 'arrivalsMode',
  DebugRevenueScore = 'debugRevenueScore',
}

export enum CustomFilterIds {
  AvailableDates = 'availableDates',
  CheckInExistsBetween = 'checkInExistsBetween',
}

export enum FilterGroup {
  properties = 'properties',
  bookings = 'bookings',
}

export const viewFromFilter: FilterConfig<any> = {
  id: ViewStateFilterIds.ViewFrom,
  label: 'View From',
  queryParamName: 'view-from',
  showOnTheTopFiltersBar: () => true,
  component: ViewFromFilter,
  defaultValue: formatDate(DEFAULT_VIEW_FROM_DATE, ISO_DATE_FORMAT),
}

export const availableDatesFilter: FilterConfig<any> = {
  id: CustomFilterIds.AvailableDates,
  label: 'Show Available',
  queryParamName: 'available-dates',
  showOnTheTopFiltersBar: () => true,
  parser: parseDateRange,
  formatter: formatDateRange,
  component: props => <AvailableDatesFilter {...props} />,
}

export const nightlyRatesFilter: FilterConfig<any> = {
  id: ViewStateFilterIds.NightlyRates,
  label: 'Nightly Rates',
  queryParamName: 'show-rates',
  ...booleanParamParser,
  showOnTheTopFiltersBar: () => true,
  component: RatesFilter,
}

export const colorByFilter: FilterConfig<any> = {
  id: ViewStateFilterIds.ColorBy,
  label: 'Color By',
  queryParamName: 'color-by',
  showOnTheTopFiltersBar: () => false,
  showOnTheAppliedFilters: () => false,
  showOnTheSidePanel: () => tapeChartGridSizingStore.isMobile,
  defaultValue: BookingsColorMode.ReservationStatus,
  component: ColorByFilter,
}

export const arrivalsModeFilter: FilterConfig<any> = {
  id: ViewStateFilterIds.ArrivalsMode,
  label: 'Arrivals Mode',
  queryParamName: 'arrivals-mode',
  ...booleanParamParser,
  component: props => (
    <ArrivalsModeFilter
      {...props}
      onChange={value => {
        storeHolder.store.tapeChartFilterApiStore.toggleArrivalMode?.(value)
      }}
    />
  ),
  defaultValue: false,
  showOnTheTopFiltersBar: () => false,
  showOnTheSidePanel: () => false,
  showOnTheAppliedFilters: () => false,
}

export const occupancyRangeFilter: FilterConfig<any> = {
  id: HomeFilterIds.Guests,
  label: 'Occupancy',
  queryParamName: 'guests',
  ...rangeParamParser,
  component: props => <GuestsRangeFilter {...props} />,
  showOnTheTopFiltersBar: () => false,
  filterPreviewFormatter: formatQuantityRangePreview,
}

export const bedroomsRangeFilter: FilterConfig<any> = {
  id: HomeFilterIds.BedroomsRange,
  label: 'Bedrooms',
  queryParamName: 'bedrooms',
  ...rangeParamParser,
  component: props => <BedroomsRangeFilter {...props} />,
  showOnTheTopFiltersBar: () => false,
  filterPreviewFormatter: formatQuantityRangePreview,
}

export const bathroomsRangeFilter: FilterConfig<any> = {
  id: HomeFilterIds.BathroomsRange,
  label: 'Bathrooms',
  queryParamName: 'bathrooms',
  ...rangeParamParser,
  component: props => <BathroomsRangeFilter {...props} />,
  showOnTheTopFiltersBar: () => false,
  filterPreviewFormatter: formatQuantityRangePreview,
}

export const amenitiesFilter: FilterConfig<any> = {
  id: HomeFilterIds.Amenities,
  label: 'Amenities',
  queryParamName: 'amenities',
  ...arrayParamsFormatterAndParser,
  ...optionsFilterPreviewFormatter(() => getValue(BookingsTapeChartStore.amenitiesOptions)),
  showOnTheTopFiltersBar: () => false,
  component: props => <AmenitiesFilter {...props} />,
}

export const fastTapeChartFiltersConfig: FilterConfig<any>[] = [
  viewFromFilter,
  availableDatesFilter,
  nightlyRatesFilter,
  colorByFilter,
  // Bookings Filters
  {
    id: ViewStateFilterIds.DepositsOnHold,
    label: 'Deposits on Hold',
    queryParamName: 'deposit-on-hold',
    component: props => <DepositsOnHoldFilter {...props} />,
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.bookings,
  },
  {
    id: ViewStateFilterIds.BookingIssues,
    label: 'Booking Issues',
    queryParamName: 'issues',
    component: BookingIssuesFilter,
    ...arrayParamsFormatterAndParser,
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.bookings,
  },
  {
    id: ViewStateFilterIds.ReservationStatus,
    label: 'Booking Status',
    queryParamName: 'reservation',
    ...arrayParamsFormatterAndParser,
    showOnTheTopFiltersBar: () => false,
    component: ReservationStatusFilter,
    groupId: FilterGroup.bookings,
  },
  {
    id: ViewStateFilterIds.PaymentStatus,
    label: 'Payment Status',
    queryParamName: 'payment-status',
    ...arrayParamsFormatterAndParser,
    component: PaymentStatusFilter,
    showOnTheTopFiltersBar: () => false,
    groupId: FilterGroup.bookings,
  },
  arrivalsModeFilter,
]
